import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import HC_more from "highcharts/highcharts-more";
import exporting from "highcharts/modules/exporting";
import ShimmerEffectLineChart from "../../ShimmerLineChart/ShimmerLinechart";
import { images } from "../../../config";

HC_more(Highcharts);
exporting(Highcharts);

const InverterMultiAxiosLineChart = ({
  LineChartData1,
  LineChartData2,
  selectIntervalReport,
  selectFirstUnit, // This prop seems unused, consider removing if not needed.
  selectSecondUnit, // This prop seems unused, consider removing if not needed.
}) => {
  const chartRef = useRef(null);

  // --- Data Initialization and Validation ---
  // Ensure LineChartData1 and LineChartData2 are always arrays
  const safeLineChartData1 = Array.isArray(LineChartData1) ? LineChartData1 : [];
  const safeLineChartData2 = Array.isArray(LineChartData2) ? LineChartData2 : [];

  // Determine if there's actual data to display
  const hasData1 = safeLineChartData1.length > 0 && safeLineChartData1[0]?.data?.length > 0;
  const hasData2 = safeLineChartData2.length > 0 && safeLineChartData2[0]?.data?.length > 0;

  // You'll need to pass tagUnit1 and tagUnit2 directly or derive them.
  // For now, I'm using placeholder values. You should replace these
  // with actual unit information from your application's state or props.
  // Example: If selectFirstUnit/selectSecondUnit provides the unit information:
  const tagUnit1 = selectFirstUnit || null;
  const tagUnit2 = selectSecondUnit || null;

  const getInterval = (unit, intervalReport) => {
    if (unit && typeof unit === "string") {
      const lowerUnit = unit.toLowerCase();
      if (lowerUnit.includes("agg") || ["kwh", "kvah", "kvarh"].includes(lowerUnit)) {
        return intervalReport;
      }
    }
    return intervalReport;
  };

  const [interval1, setInterval1] = useState(() =>
    getInterval(tagUnit1, selectIntervalReport)
  );

  const [interval2, setInterval2] = useState(() =>
    getInterval(tagUnit2, selectIntervalReport)
  );

  useEffect(() => {
    setInterval1(getInterval(tagUnit1, selectIntervalReport));
    setInterval2(getInterval(tagUnit2, selectIntervalReport));
  }, [selectIntervalReport, tagUnit1, tagUnit2]);

  // Determine which line has more data points for categories
  const isFirstLineLonger =
    (hasData1 ? safeLineChartData1[0].data.length : 0) >=
    (hasData2 ? safeLineChartData2[0].data.length : 0);

  // --- Categories Calculation (Fixed) ---
  // Ensure we only try to map if data exists
  const categories = (() => {
    if (isFirstLineLonger && hasData1) {
      return safeLineChartData1[0].data.map((row) => row.MIN_KEY);
    } else if (hasData2) {
      return safeLineChartData2[0].data.map((row) => row.MIN_KEY);
    }
    return []; // Return empty array if no data is available
  })();

  const baseInterval = Math.min(interval1, interval2);

  const calculateDataPoints = (data, interval, baseInterval) => {
    if (!data || data.length === 0) return [];

    const scaledData = [];
    const scalingFactor = interval / baseInterval;

    for (let i = 0; i < data.length; i++) {
      const x = i * scalingFactor; // precise x-coordinate
      scaledData.push({
        x: x,
        y: parseFloat(data[i].tag_value),
      });
    }
    return scaledData;
  };

  const firstSeriesData =
    hasData1 && (!hasData2 || Number(interval1) === 1 || Number(interval2) === 1)
      ? safeLineChartData1[0].data.map((row) => parseFloat(row.tag_value))
      : hasData1
      ? calculateDataPoints(safeLineChartData1[0].data, interval1, baseInterval)
      : [];

  const secondSeriesData =
    hasData2 && (!hasData1 || Number(interval1) === 1 || Number(interval2) === 1)
      ? safeLineChartData2[0].data.map((row) => parseFloat(row.tag_value))
      : hasData2
      ? calculateDataPoints(safeLineChartData2[0].data, interval2, baseInterval)
      : [];

  // --- Placeholder for unit colors ---
  // You need to define how `findUnitColor1` and `findUnitColor2` will determine colors.
  // This likely comes from a global config, context, or another prop.
  // For demonstration, they are set to simple objects with a color property.
  const findUnitColor1 = { color: "#9A64B2" }; // Example color for first series
  const findUnitColor2 = { color: "#E579A0"}; // Example color for second series

  const options = {
    chart: {
      type: "spline",
      
    },
    title: {
      text: null,
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      categories: categories,
      scrollbar: {
        enabled: true,
      },
    },
    series: [
      {
        name: hasData1 ? safeLineChartData1[0]["tag_name"] : "",
        data: firstSeriesData,
        color: findUnitColor1 ? findUnitColor1.color : "",
        yAxis: 0,
        pointStart: 0,
        ...(hasData1 &&
        hasData2 &&
        (Number(interval1) === 1 || Number(interval2) === 1)
          ? { pointInterval: Number(interval1) }
          : {}),
      },
      {
        name: hasData2 ? safeLineChartData2[0]["tag_name"] : "",
        data: secondSeriesData,
        color: findUnitColor2 ? findUnitColor2.color : "",
        yAxis: 1,
        pointStart: 0,
        ...(hasData1 &&
        hasData2 &&
        (Number(interval1) === 1 || Number(interval2) === 1)
          ? { pointInterval: Number(interval2) }
          : {}),
      },
    ],

    yAxis: [
      {
        title: {
          text: hasData1 ? safeLineChartData1[0]["tag_name"] : "",
        },
        opposite: false,
        alignTicks: false,
      },
      {
        title: {
          text: hasData2 ? safeLineChartData2[0]["tag_name"] : "",
        },
        opposite: true,
        alignTicks: false,
      },
    ],

    exporting: {
      enabled: true,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      formatter: function () {
        let s = `${this.x}<br/>`;
        this.points.forEach((point) => {
          s += `<br/><span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: <b>${point.y}</b>`;
        });
        return s;
      },
    },
  };

  

  // Assuming `shimmer` is now determined internally or passed differently.
  // For this example, I'm setting it based on the absence of data.
  const shimmer = !hasData1 && !hasData2;

  return (
    <>
      {!shimmer ? (
        hasData1 || hasData2 ? ( // Check if any data is present for rendering chart
          <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
        ) : (
          <img
            src={images.noDataFoundImg}
            className="notFoundDataImg"
            alt="No Data Found Img"
            style={{ paddingTop: "100px" }}
          />
        )
      ) : (
        <ShimmerEffectLineChart />
      )}
    </>
  );
};

export default InverterMultiAxiosLineChart;